/**
 * Shared types for the notifications surface (ADR-0044).
 * Spec: governance-foundation §8 / §10 / §12.
 *
 * The existing console convention (see TenantAdminPeople.tsx etc.) is to
 * call `apiFetch<T>(path, opts?)` inline per-route, not via wrapper
 * functions. This module exports TYPES only — components construct their
 * own fetch calls against `/api/tenant/notifications/*`.
 */

/**
 * ADR-0154 §6.3 C2 — the hub owns this vocabulary and SERVES it. These unions
 * exist so console code can name a value it handles specially; they are never
 * the list a surface iterates. `subscription` was missing from the category
 * union and `report_subscription` from the type union, which is precisely how
 * the preferences page came to hide a preference from every user.
 */
export type NotificationCategory = "transactional" | "system" | "digest" | "subscription";

export type NotificationType =
  | "action_item_created"
  | "action_item_refired"
  | "action_item_auto_resolved"
  | "action_item_status_changed"
  | "action_item_escalated"
  | "rasci_assigned"
  | "watch_email"
  | "watch_fired"
  | "system_alert"
  | "digest_open_action_items"
  | "report_subscription";

export type NotificationStatus = "unread" | "read" | "dismissed" | "purged";

export interface Notification {
  id: string;
  tenant_id: string;
  account_id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  body: string;
  entity_type: string | null;
  entity_id: string | null;
  on_behalf_of_account_id: string | null;
  on_behalf_of_display_name: string | null;
  status: NotificationStatus;
  read_at: string | null;
  dismissed_at: string | null;
  purged_at: string | null;
  created_at: string;
  /**
   * Structured rows for a collapsed event — one watch evaluation that fired
   * many rows. Empty for a single-event notification, whose `body` says
   * everything. When present the detail view renders these instead of `body`:
   * the body is the EMAIL's wording, and printing it in the app produced dead
   * link text and sentences pointing the reader at the inbox they were already
   * looking at.
   */
  lines: NotificationLine[];
  /** Underlying rows represented. Exceeds `lines.length` only when the
   *  producer itself truncated. */
  total_count: number | null;
}

/** One row of a collapsed event. `url_path` is app-relative. */
export interface NotificationLine {
  title: string;
  detail?: string | null;
  severity?: string | null;
  url_path?: string | null;
  action_item_id?: string | null;
}

export interface NotificationListCursor {
  created_at: string;
  id: string;
}

export interface NotificationListResponse {
  items: Notification[];
  next_cursor: NotificationListCursor | null;
}

/** ADR-0171: the user's per-type election — 'default' = follow tenant routing. */
export type UserEmailMode = "default" | "immediate" | "bundled" | "digest" | "none";

/** ADR-0171: the tenant routing modes (the matrix's cell vocabulary). */
export type DeliveryMode = "immediate" | "bundled" | "digest" | "none";

export interface NotificationPreference {
  tenant_id: string;
  account_id: string;
  notification_type: NotificationType;
  /** ADR-0171 — replaces the old email_enabled boolean. */
  email_mode: UserEmailMode;
  in_system_enabled: boolean;
  updated_at: string;
}

/** ADR-0171 §5.3 — one row per hub-served type: its allowed elections and the
 *  words for its default are SERVED, never hardcoded here (drift doctrine). */
export interface ServedNotificationType {
  type: string;
  category: string;
  allowed_modes: UserEmailMode[];
  default_mode_label: string | null;
}

/** ADR-0171 — per-user digest schedule override + quiet hours. NULLs inherit. */
export interface NotificationUserSettings {
  tenant_id: string;
  account_id: string;
  digest_cadence: "daily" | "weekly" | null;
  digest_day_of_week: number | null;
  digest_hour: number | null;
  quiet_hours_start: number | null;
  quiet_hours_end: number | null;
  updated_at: string;
}

export interface TenantNotificationContext {
  floors_effective: Record<string, DeliveryMode>;
  digest_effective: { cadence: "daily" | "weekly"; day_of_week: number; hour: number };
  timezone: string;
}

export interface NotificationPreferencesResponse {
  preferences: NotificationPreference[];
  types: ServedNotificationType[];
  settings: NotificationUserSettings | null;
  tenant: TenantNotificationContext;
}

/** Human labels for the user's election, one home for every select. */
export const EMAIL_MODE_LABELS: Record<UserEmailMode, string> = {
  default: "Default",
  immediate: "Immediately",
  bundled: "Bundled",
  digest: "Digest",
  none: "No email"
};

export const DAY_OF_WEEK_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
] as const;

/** "8:00 AM" for an hour-of-day; the label alongside says whose clock. */
export function hourLabel(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:00 ${hour < 12 ? "AM" : "PM"}`;
}

/**
 * Display metadata for the preferences page. Groups types by category and
 * provides human labels + help text. System-category types are rendered as
 * disabled toggles (spec §13.4).
 */
/**
 * ADR-0154 §6.3 C2 — a presentation OVERLAY, not a roster.
 *
 * This map used to be the list the preferences page iterated, so a type absent
 * from it was invisible in the UI — not mislabelled, unmanageable. The page now
 * renders one row per SERVED type and consults this only for nicer wording;
 * anything it does not cover is humanized. Partial by design, and it can no
 * longer hide anything.
 */
export const NOTIFICATION_TYPE_META: Partial<Record<
  NotificationType,
  { label: string; category: NotificationCategory; description: string }
>> = {
  action_item_created: {
    label: "Action item created",
    category: "transactional",
    description: "A new action item is created and you are in its RASCI."
  },
  action_item_refired: {
    label: "Action item re-fired",
    category: "transactional",
    description: "A watch condition fired again while its action item was still open."
  },
  action_item_auto_resolved: {
    label: "Action item auto-resolved",
    category: "transactional",
    description:
      "A watch condition that was firing has cleared, and the action item closed automatically. " +
      "Only delivered when the watch's response has 'Notify when auto-resolved' enabled."
  },
  action_item_status_changed: {
    label: "Action item status changed",
    category: "transactional",
    description: "An action item you own or are accountable for changed status."
  },
  action_item_escalated: {
    label: "Action item escalated",
    category: "transactional",
    description:
      "An action item did not transition within its policy window and was escalated up the " +
      "escalates-to chain. You can opt out if you don't want to be paged on escalations."
  },
  rasci_assigned: {
    label: "RASCI assignment",
    category: "transactional",
    description: "You were added to an action item's RASCI after creation."
  },
  watch_email: {
    label: "Watch email",
    category: "transactional",
    description:
      "A watch operation's email response fired and you were a recipient. Subject and body are " +
      "author-specified on the response node."
  },
  system_alert: {
    label: "System alerts",
    category: "system",
    description: "Platform-wide alerts. Cannot be disabled."
  },
  digest_open_action_items: {
    label: "Open items digest",
    category: "digest",
    description: "A summary of your open action items, sent on your digest schedule."
  }
};

/**
 * ADR-0154 §6.3 C2 — decided from the SERVED category, never from the local
 * overlay. Callers pass the category the hub sent for this type.
 */
export function isSystemCategory(category: string): boolean {
  return category === "system";
}

/** Presentation for one served notification type; humanized when unknown here. */
export function notificationTypeDisplay(
  type: string,
  category: string
): { label: string; description: string; category: string } {
  const meta = NOTIFICATION_TYPE_META[type as NotificationType];
  if (meta) return { label: meta.label, description: meta.description, category };
  return {
    label: type.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
    description: "",
    category,
  };
}
